import * as Sentry from '@sentry/nextjs';

import { levelOrder } from './sentry-log-config';

export type LogAttrs = Record<string, unknown>;
export type LogLevel = keyof typeof levelOrder;

const DEFAULT_CONSOLE_LEVEL: LogLevel = 'warn';

function isValidLogLevel(value: string): value is LogLevel {
	return value in levelOrder;
}

const MIN_CONSOLE_LEVEL: LogLevel = (() => {
	const envLevel = process.env.NEXT_PUBLIC_CONSOLE_LOG_LEVEL;
	if (envLevel && isValidLogLevel(envLevel)) {
		return envLevel;
	}
	return DEFAULT_CONSOLE_LEVEL;
})();
function shouldConsoleLog(level: LogLevel): boolean {
	return levelOrder[level] >= levelOrder[MIN_CONSOLE_LEVEL];
}

function logToConsole(
	level: LogLevel,
	message: string,
	attrs?: LogAttrs
): void {
	if (!shouldConsoleLog(level)) return;

	const safeAttrs = attrs ?? {};

	switch (level) {
		case 'trace':
		case 'debug':
			console.debug(message, safeAttrs);
			break;
		case 'info':
			console.info(message, safeAttrs);
			break;
		case 'warn':
			console.warn(message, safeAttrs);
			break;
		case 'error':
		case 'fatal':
			console.error(message, safeAttrs);
			break;
	}
}

function logToSentry(level: LogLevel, message: string, attrs?: LogAttrs): void {
	switch (level) {
		case 'trace':
			Sentry.logger.trace(message, attrs);
			break;
		case 'debug':
			Sentry.logger.debug(message, attrs);
			break;
		case 'info':
			Sentry.logger.info(message, attrs);
			break;
		case 'warn':
			Sentry.logger.warn(message, attrs);
			break;
		case 'error':
			Sentry.logger.error(message, attrs);
			break;
		case 'fatal':
			Sentry.logger.fatal(message, attrs);
			break;
	}
}

export const logger = {
	trace(message: string, attrs?: LogAttrs): void {
		logToConsole('trace', message, attrs);
		logToSentry('trace', message, attrs);
	},
	debug(message: string, attrs?: LogAttrs): void {
		logToConsole('debug', message, attrs);
		logToSentry('debug', message, attrs);
	},
	info(message: string, attrs?: LogAttrs): void {
		logToConsole('info', message, attrs);
		logToSentry('info', message, attrs);
	},
	warn(message: string, attrs?: LogAttrs): void {
		logToConsole('warn', message, attrs);
		logToSentry('warn', message, attrs);
	},
	error(message: string, attrs?: LogAttrs): void {
		logToConsole('error', message, attrs);
		logToSentry('error', message, attrs);
	},
	fatal(message: string, attrs?: LogAttrs): void {
		logToConsole('fatal', message, attrs);
		logToSentry('fatal', message, attrs);
	},
};
